/**
 * Parent sign-in. Cognito, authorization code flow with PKCE, no library.
 *
 * Children never have accounts and never reach this file. The only identity
 * in Valley is an adult account holder, which is the point of the design --
 * see infra/AUTHORIZATION.md.
 *
 * WHERE THE TOKENS LIVE, and why:
 *
 * The access token is held in memory only. It lasts sixty minutes and a page
 * reload throws it away, which is right -- it is the thing that can read a
 * family's ledger.
 *
 * The refresh token goes in sessionStorage, so it survives a reload but not
 * closing the tab. It is NOT a constant to relax when somebody complains
 * about signing in, and an earlier version of this comment said it was.
 *
 * This is what keeps a child out of a parent's account on a shared family
 * computer, which is a machine this product is specifically built for. The
 * child already uses this origin -- `/me` holds a durable bearer token in
 * localStorage, because a child should not have to type anything to see
 * their own valley. If the parent's session persisted the same way, a child
 * opening the browser would land in the parent's account holding the ledger,
 * every name, the device list, the family code and the delete button.
 *
 * sessionStorage is per TAB. A child opening a new one gets an empty store,
 * no refresh token, and the parent's Cognito password in the way. The family
 * key sitting in IndexedDB does not help them either: it decrypts
 * ciphertext, and fetching any ciphertext needs a session they do not have.
 * That is also why DECISIONS.md section 16 can safely leave the key on the
 * device -- the two decisions hold each other up, and moving this one alone
 * breaks the pair. See section 19.
 *
 * localStorage would additionally mean a script-injection bug walks away
 * with thirty days of access rather than one tab's worth.
 *
 * Refresh token rotation is enabled on the app client (an Essentials feature,
 * see 20-api), so a refresh token reused after rotation is a stolen one and
 * Cognito can tell.
 */

import { native } from '../native/platform';
import { assertKnownOrigin, config } from '../config';
import { challengeFor, createState, createVerifier, pkceStore } from './pkce';
import { refreshTokens, revoke, type Tokens } from './direct';
import {
  forgetPin,
  setPin,
  unlockWithPin,
  type Sealer,
  type UnlockOutcome,
} from './pin';

const REFRESH_KEY = 'valley.auth.refresh';
/**
 * Which refresh-token chain this tab holds: 'pin' when it came from a PIN
 * unlock (or a PIN was set on it), 'password' after a password sign-in.
 *
 * It matters because Cognito ROTATES refresh tokens: every refresh spends the
 * token and hands back a new one. A 'pin' chain is the same chain as the
 * locked record, so every rotation must be re-sealed into that record -- and
 * re-sealing needs the key derived from the PIN, which only exists after the
 * PIN was typed in this page's lifetime. Refreshing a 'pin' chain WITHOUT it
 * (a reload, or following a link from a child's page back to `/`) used to
 * spend the very token the record held: the next PIN decrypted perfectly,
 * Cognito refused what it opened, and the account was dropped from the
 * picker. See `restore`.
 */
const CHAIN_KEY = 'valley.auth.chain';

/**
 * Which account the keys on this device belong to.
 *
 * localStorage, not sessionStorage: the whole point is to outlive the session
 * that wrote it, so the NEXT sign-in can be compared against it.
 */
const ACCOUNT_KEY = 'valley.auth.account';

export interface Session {
  accessToken: string;
  idToken: string;
  expiresAt: number;
}

let session: Session | null = null;

/**
 * The right to re-seal this account's locked record, held while unlocked.
 *
 * Non-null only after a PIN has been entered or set THIS session, which is
 * the only window in which we are entitled to rewrite that record. A parent
 * who signed in with their password instead has a different refresh-token
 * chain, and overwriting the locked one with it would be wrong -- see the
 * note on `exchange`.
 */
let sealer: Sealer | null = null;

function redirectUri(): string {
  // In the app, Cognito comes back to the app's own scheme, which the app
  // turns into this bundle's /auth/callback; see native/platform.ts.
  return native()?.callbackUri ?? `${window.location.origin}/auth/callback`;
}

/** Leave for a Cognito page: this tab in a browser, the system sheet in the app. */
function leaveFor(url: string): void {
  const shell = native();
  if (shell) void shell.openAuth(url);
  else window.location.assign(url);
}

/**
 * Email-and-password sign-in, on Birbly's own screen (screens/SignInScreen,
 * auth/direct.ts) -- never a browser, in the app or out of it (2026-10-19).
 * The hosted Cognito page is now only the way through to Google and Apple.
 *
 * `fresh` is kept for the callers that pass it ("use another account"): the
 * in-app form never remembers anybody, so every visit is fresh.
 */
export async function signIn(_options: { fresh?: boolean } = {}): Promise<void> {
  window.location.assign('/sign-in');
}

/** Straight to creating an account, on the same screen. */
export async function signUp(): Promise<void> {
  window.location.assign('/sign-up');
}

/**
 * The tokens from an in-app sign-in, carried across one page load.
 *
 * The sign-in screen hands them over and reloads at `/`, so the app starts
 * exactly as it does after the hosted page's redirect -- one way in, not two.
 * sessionStorage (this tab only) and taken on first read; the refresh token
 * lives there anyway (see the note at the top of this file).
 */
const HANDOFF_KEY = 'valley.auth.handoff';

export function finishDirectSignIn(tokens: Tokens): void {
  sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(tokens));
  window.location.replace('/');
}

function takeHandoff(): Session | null {
  const raw = sessionStorage.getItem(HANDOFF_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(HANDOFF_KEY);
  try {
    const tokens = JSON.parse(raw) as Tokens;
    if (!tokens.AccessToken || !tokens.IdToken) return null;
    // A password sign-in starts its own chain, like the hosted page's.
    sessionStorage.setItem(CHAIN_KEY, 'password');
    return adopt({
      access_token: tokens.AccessToken,
      id_token: tokens.IdToken,
      expires_in: tokens.ExpiresIn,
      ...(tokens.RefreshToken ? { refresh_token: tokens.RefreshToken } : {}),
    });
  } catch {
    return null;
  }
}

/**
 * Straight to Google, through Cognito (`identity_provider=Google` skips the
 * managed login page). The same call signs a parent in or signs them up:
 * Cognito creates the account the first time it sees that Google login.
 *
 * Google is a broker for PARENTS only. Children never sign in here -- they
 * use the PIN on a device a parent has already signed in.
 */
export async function signInWithGoogle(): Promise<void> {
  return signInWithProvider('Google');
}

/** Sign in with Apple, the same way (20-api AppleIdentityProvider). */
export async function signInWithApple(): Promise<void> {
  return signInWithProvider('SignInWithApple');
}

export type IdentityProvider = 'Google' | 'SignInWithApple';
const PROVIDER_KEY = 'birbly.identity-provider';

/**
 * Whether a parent has signed in with Google on this device before, so the
 * button can say "Continue with Google" to them and "Sign up with Google" to
 * somebody new (2026-10-19). Set from the ID token's `identities` claim, which
 * Cognito adds only for a federated sign-in -- not from the button press,
 * which may have been abandoned. Not a secret and not an account: just which
 * words to show.
 */
const GOOGLE_HERE_KEY = 'birbly.google-here';

export function googleUsedHere(): boolean {
  try {
    return localStorage.getItem(GOOGLE_HERE_KEY) === '1';
  } catch {
    return false;
  }
}

function noteProvider(idToken: string): void {
  const identities = claimsOf(idToken)?.identities;
  if (!Array.isArray(identities)) return;
  if (identities.some((one) => (one as { providerName?: string })?.providerName === 'Google')) {
    try {
      localStorage.setItem(GOOGLE_HERE_KEY, '1');
    } catch {
      // Only the button's wording depends on it.
    }
  }
}

async function signInWithProvider(provider: IdentityProvider): Promise<void> {
  // Remembered for the ACCOUNT_LINKED retry below, which has to go round
  // through the same provider. localStorage, not sessionStorage: in the app
  // the system can stop the web view while the sign-in sheet is up.
  try {
    localStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    // Without it the retry goes through Google, the first provider.
  }
  return go('/oauth2/authorize', { identity_provider: provider });
}

function lastProvider(): IdentityProvider {
  try {
    return localStorage.getItem(PROVIDER_KEY) === 'SignInWithApple' ? 'SignInWithApple' : 'Google';
  } catch {
    return 'Google';
  }
}

/**
 * The pre-sign-up trigger in 20-api links a first Google sign-in to an
 * existing email-and-password account, and then has to fail that one sign-in
 * (Cognito would otherwise make a second, separate user). The failure comes
 * back carrying ACCOUNT_LINKED, and simply going round again signs the parent
 * into their existing account. Once per tab, so a trigger that keeps failing
 * can never loop.
 */
const LINK_RETRY_KEY = 'birbly.google-link-retry';

async function go(path: string, extra: Record<string, string> = {}): Promise<void> {
  const verifier = createVerifier();
  const state = createState();
  pkceStore.save(verifier, state);

  const url = new URL(`${config.cognito.domain}${path}`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.cognito.clientId);
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', await challengeFor(verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  for (const [name, value] of Object.entries(extra)) url.searchParams.set(name, value);

  assertKnownOrigin(url.toString());
  leaveFor(url.toString());
}

/**
 * Handle the redirect back. Returns null when this is not a callback.
 *
 * The state check is not ceremony. Without it, an attacker can hand someone a
 * link carrying the attacker's own authorization code and quietly sign them
 * into the attacker's account.
 */
export async function completeSignIn(): Promise<Session | null> {
  const handed = takeHandoff();
  if (handed) return handed;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const error = params.get('error');

  if (error) {
    const description = params.get('error_description') ?? error;
    if (description.includes('ACCOUNT_LINKED') && !sessionStorage.getItem(LINK_RETRY_KEY)) {
      sessionStorage.setItem(LINK_RETRY_KEY, '1');
      await signInWithProvider(lastProvider());
      // The page is navigating away; never settle, so nothing flashes first.
      return new Promise<Session | null>(() => {});
    }
    throw new Error(description.replace(/^PreSignUp failed with error\s*/i, ''));
  }
  if (!code) return null;

  const { verifier, state } = pkceStore.take();
  if (!verifier) {
    throw new Error('This sign-in was not started on this device. Please sign in again.');
  }
  if (!returnedState || returnedState !== state) {
    throw new Error('That sign-in could not be verified. Please try again.');
  }

  const tokens = await exchange({
    grant_type: 'authorization_code',
    client_id: config.cognito.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  // A password sign-in starts its own chain, separate from any locked
  // record's, so this tab may refresh it freely.
  sessionStorage.setItem(CHAIN_KEY, 'password');
  sessionStorage.removeItem(LINK_RETRY_KEY);

  // Back to the root, dropping both the code and the callback path.
  //
  // Dropping the query alone left the address bar on /auth/callback, which is
  // not a page -- it is the one-shot landing spot for an OAuth redirect. A
  // parent who bookmarks it, shares it or just reloads gets a URL that only
  // ever worked once, and the app has no route of that name to explain it.
  //
  // replaceState rather than pushState, so the code never enters history at
  // all and Back does not return to it.
  window.history.replaceState({}, '', '/');
  return tokens;
}

export async function restore(): Promise<Session | null> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  // A PIN chain with no way to re-seal it: ask for the PIN rather than spend
  // the token the locked record depends on. The PIN prompt opens the record,
  // which holds this same chain, and the session carries on from there.
  if (sessionStorage.getItem(CHAIN_KEY) === 'pin' && !sealer) {
    lock();
    return null;
  }
  try {
    return await refreshWith(refreshToken);
  } catch {
    sessionStorage.removeItem(REFRESH_KEY);
    return null;
  }
}

/**
 * New tokens for a refresh token: the token endpoint, as always, and the
 * API's GetTokensFromRefreshToken when the endpoint refuses -- which is how
 * AWS documents refreshing a token that came from the API (the in-app
 * sign-in) on a client with rotation on. A refusal from both is a dead token
 * (status 400, as callers expect); anything else is the network.
 */
async function refreshWith(refreshToken: string): Promise<Session> {
  try {
    return await exchange({
      grant_type: 'refresh_token',
      client_id: config.cognito.clientId,
      refresh_token: refreshToken,
    });
  } catch (problem) {
    if ((problem as { status?: number }).status !== 400) throw problem;
    let tokens: Tokens;
    try {
      tokens = await refreshTokens(refreshToken);
    } catch (second) {
      const status = (second as { status?: number }).status;
      throw Object.assign(new Error('Sign-in failed. Please try again.'), { status: status && status < 500 ? 400 : status });
    }
    return adopt({
      access_token: tokens.AccessToken,
      id_token: tokens.IdToken,
      expires_in: tokens.ExpiresIn,
      ...(tokens.RefreshToken ? { refresh_token: tokens.RefreshToken } : {}),
    });
  }
}

/** A valid access token, refreshed when it is close to expiry. */
export async function accessToken(): Promise<string | null> {
  if (session && session.expiresAt - Date.now() > 60_000) return session.accessToken;
  const refreshed = await restore();
  return refreshed?.accessToken ?? null;
}

export function currentSession(): Session | null {
  return session;
}

/**
 * Who is signed in, as the locked record needs to name them.
 *
 * `sub` and not email is the identity here: an email can be changed in
 * Cognito and `sub` cannot, so a record keyed by email would silently detach
 * from its account. The email rides along only to be shown in the picker.
 */
export function currentAccount(): { sub: string; email: string } | null {
  const claims = claimsOf(session?.idToken);
  const sub = claims?.sub;
  if (typeof sub !== 'string' || !sub) return null;
  const email = typeof claims?.email === 'string' ? claims.email : '';
  return { sub, email };
}

/**
 * Put this session behind a PIN, so it can be resumed without a password.
 *
 * Needs a refresh token to seal, which means it can only be offered to a
 * parent who is signed in right now -- there is deliberately no path that
 * creates a PIN for an account that is not present.
 */
export async function enablePin(pin: string): Promise<void> {
  const account = currentAccount();
  if (!account) throw new Error('Sign in before setting a PIN.');
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (!refreshToken) {
    throw new Error('This session cannot be locked. Sign in again and retry.');
  }
  sealer = await setPin(pin, account, refreshToken);
  sessionStorage.setItem(CHAIN_KEY, 'pin');
}

/**
 * Resume a locked account with its PIN.
 *
 * Two different failures, kept apart on purpose. A WRONG PIN never reached
 * the network and leaves the record alone bar the attempt count. A STALE
 * TOKEN means the PIN was right and Cognito refused what it opened -- thirty
 * days elapsed, or the token was revoked elsewhere -- and the only honest
 * thing to do is drop the record and ask for a full sign-in. Reporting that
 * as a wrong PIN would have a parent typing correct digits into a prompt
 * that could never accept them.
 */
export async function signInWithPin(
  sub: string,
  pin: string,
): Promise<UnlockOutcome> {
  const opened = await unlockWithPin(sub, pin);
  if (opened.status !== 'ok') return opened;

  try {
    sessionStorage.setItem(REFRESH_KEY, opened.refreshToken);
    sessionStorage.setItem(CHAIN_KEY, 'pin');
    // Set BEFORE the exchange, so the rotated token that comes back is
    // re-sealed by the hook in `exchange` rather than being dropped.
    sealer = opened.sealer;
    await refreshWith(opened.refreshToken);
    return opened;
  } catch (problem) {
    sealer = null;
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(CHAIN_KEY);
    // Only Cognito saying the token is no good ends the record. A dropped
    // connection, a timeout or a server error is not the parent's fault and
    // must not cost them their PIN: it throws, the record stays, and the
    // prompt says "try again".
    const status = (problem as { status?: number }).status;
    if (status === 400 || status === 401) {
      forgetPin(sub);
      return { status: 'gone' };
    }
    throw problem;
  }
}

/**
 * End the session, keep the account in the picker.
 *
 * This is what the idle timer and "Switch account" call, and it is the point
 * of the whole feature: the tokens in memory and in `sessionStorage` go, so a
 * child who walks up to an open tab finds a PIN prompt -- and the parent gets
 * back in with six digits instead of a password. No redirect, because there
 * is nothing to tell Cognito about: the locked record is a local artefact.
 */
export function lock(): void {
  session = null;
  sealer = null;
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(CHAIN_KEY);
}

export function signOut(): void {
  const account = currentAccount();
  // Signing out is the opposite of locking: it says "take me off this
  // browser". The logout below revokes the refresh token, so leaving the
  // locked record behind would offer a PIN prompt that opens a dead token
  // and drops the parent into a full sign-in anyway, one prompt later.
  if (account) forgetPin(account.sub);
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  session = null;
  sealer = null;
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(CHAIN_KEY);
  // Revoked through the API, so the token is dead whichever way it was
  // issued. In the app that is the whole of signing out: no browser sheet
  // flashing up to visit Cognito's logout page. Best effort -- signing out
  // must work offline, and the token is gone from this device regardless.
  const revoked = refreshToken ? revoke(refreshToken).catch(() => undefined) : Promise.resolve();
  if (native()) {
    void revoked.finally(() => window.location.replace('/'));
    return;
  }
  // On the website, the hosted page's own session (a parent who came through
  // Google) is ended too, so a shared computer does not keep it.
  const url = new URL(`${config.cognito.domain}/logout`);
  url.searchParams.set('client_id', config.cognito.clientId);
  url.searchParams.set('logout_uri', native()?.signedOutUri ?? `${window.location.origin}/`);
  assertKnownOrigin(url.toString());
  leaveFor(url.toString());
}

async function exchange(body: Record<string, string>): Promise<Session> {
  const endpoint = `${config.cognito.domain}/oauth2/token`;
  assertKnownOrigin(endpoint);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
  if (!response.ok) {
    // The status rides along so a caller can tell "Cognito refused this
    // token" (400) from "the network had a moment".
    throw Object.assign(new Error('Sign-in failed. Please try again.'), { status: response.status });
  }
  return adopt((await response.json()) as TokenPayload);
}

interface TokenPayload {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

/** Hold a fresh set of tokens, from the token endpoint or the API alike. */
function adopt(payload: TokenPayload): Session {

  // A refresh grant does not always return a new refresh token; keep the one
  // already held when it does not.
  if (payload.refresh_token) {
    sessionStorage.setItem(REFRESH_KEY, payload.refresh_token);
    // ROTATION. Using a stored refresh token spends it, so the replacement
    // has to take its place in the locked record or the next PIN unlock
    // decrypts perfectly and presents a token Cognito has already retired --
    // a failure that looks exactly like a wrong PIN and is not one.
    //
    // Only when a sealer is held, which means a PIN was entered or set this
    // session. A password sign-in starts a SEPARATE refresh-token chain, and
    // writing that one over the locked record would quietly invalidate the
    // other tab that is still using it.
    if (sealer) {
      const rotated = payload.refresh_token;
      void sealer.reseal(rotated).catch(() => {
        // The record is now stale and the next unlock will fail into a full
        // sign-in. That is recoverable and worth nothing louder: this runs
        // inside an ordinary token refresh, and throwing here would break a
        // working session over a storage write.
      });
    }
  }
  noteProvider(payload.id_token);
  session = {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
  return session;
}

/**
 * The email address this parent signed in with.
 *
 * Read out of the ID token WITHOUT verifying the signature, which is safe only
 * because of what it is used for: showing a person their own email, and
 * sealing it into an invitation so the parent receiving it can see who sent
 * it. Nothing is authorised on the strength of this. Every authorisation
 * decision in the system is made by the API from the ACCESS token, which the
 * gateway verifies properly against Cognito's JWKS.
 *
 * Null rather than throwing when the claim is missing. A parent who signed up
 * before the pool asked for an email has no email claim, and an invitation
 * that simply omits the sender is better than one that cannot be created.
 */
export function signedInEmail(): string | null {
  const email = claimsOf(session?.idToken)?.email;
  return typeof email === 'string' ? email : null;
}

/**
 * An id token's claims, WITHOUT verifying the signature.
 *
 * Safe here and nowhere else. Nothing read out of this decides what anybody
 * is allowed to do: the API verifies the token itself on every request, so a
 * forged claim buys nothing. It answers two local questions -- who to show in
 * the corner of the screen, and whether the keys in this browser belong to
 * the person now signing in.
 *
 * One decoder, because there were briefly two. The second was added for
 * `noteAccount` and differed in a way that mattered: it padded the base64
 * before decoding and this one did not, so the two disagreed about tokens
 * whose payload length was not a multiple of four. They are the same tokens.
 */
function claimsOf(idToken: string | undefined): Record<string, unknown> | null {
  if (!idToken) return null;
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === 'object'
      ? (claims as Record<string, unknown>)
      : null;
  } catch {
    // A malformed token is a sign-in problem and will surface as one on the
    // next request. It is not worth failing a caller over.
    return null;
  }
}


/**
 * The `sub` claim, read without verifying the signature.
 *
 * That is safe here and would not be anywhere else. This value never decides
 * what anybody is allowed to do -- the API verifies the token itself on every
 * request, and a forged `sub` planted in localStorage would grant nothing. It
 * decides one local question: are the family keys in this browser's vault the
 * ones the person now signing in put there? Getting that wrong wipes some
 * cached keys and costs one re-entry of the family code.
 */
function subjectOf(idToken: string): string | null {
  const sub = claimsOf(idToken)?.sub;
  return typeof sub === 'string' && sub ? sub : null;
}

/**
 * Record who this session belongs to. True when that is somebody new.
 *
 * SIGNING OUT DELIBERATELY LEAVES THE FAMILY KEY on the device, because signed
 * in and unlocked are different things and ending one should not end the
 * other. That is right for the same parent coming back. It is wrong the moment
 * a DIFFERENT parent signs in on the same browser: they inherit a vault full
 * of keys for a family they may not even be in, and the app reaches a state it
 * has no screen for -- which is how a second sign-up landed on a permanent
 * "One moment."
 *
 * Nothing was recording who the keys belonged to, so nothing could notice. Now
 * something does.
 *
 * Returns false on the first ever sign-in: there is nothing to have changed
 * from, and wiping an empty vault would only cost a pointless family-code
 * prompt.
 */
export function noteAccount(session: Session): boolean {
  const sub = subjectOf(session.idToken);
  if (!sub) return false;
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(ACCOUNT_KEY);
    localStorage.setItem(ACCOUNT_KEY, sub);
  } catch {
    // Private mode, or storage turned off. Losing the record means losing the
    // check, not breaking sign-in.
    return false;
  }
  return previous !== null && previous !== sub;
}
